import axiosApiClient from '..';
import { handleError, throwErrorIfWrongStatus } from './utils';

const BASE_URL = '/d/user-influencer-collections';

class CollectionService {
  fetchCollections = async influencerId => {
    try {
      const params = new URLSearchParams();
      if (influencerId) {
        params.append('influencerId', influencerId);
      }
      const response = await axiosApiClient.get(`${BASE_URL}?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data } = response.data.data;
      return { data };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching collections.');
    }
  };

  createCollection = async (name, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) {
        params.append('influencerId', influencerId);
      }
      const response = await axiosApiClient.post(`${BASE_URL}?${params.toString()}`, { name });
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while creating collection.');
    }
  };

  fetchCollectionUsers = async (collectionId, page, search, influencerId) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('search', search);

      if (influencerId) {
        params.append('influencerId', influencerId);
      }
      const response = await axiosApiClient.get(`${BASE_URL}/${collectionId}?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data, total, perPage: perPageResponse } = response.data.data;
      return { data, maxReached: total <= +page * +perPageResponse, page };
    } catch (err) {
      throw handleError(err, 'Error occurred while collection users.');
    }
  };

  fetchInfluencerChats = async (page, search, collectionId, influencerId) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      if (search) params.append('search', search);
      if (collectionId) params.append('collectionId', collectionId);
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.get(`${BASE_URL}/chats?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data, total, perPage: perPageResponse } = response.data.data;
      return { data, maxReached: total <= +page * +perPageResponse, page, collectionId: collectionId || null };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching influencer chats.');
    }
  };

  fetchInfluencerChat = async (cursor, userInfluencerId, influencerId, bidirectional) => {
    try {
      const params = new URLSearchParams();
      if (cursor) params.append('cursor', cursor);
      if (bidirectional) params.append('bidirectional', true);
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.get(
        `${BASE_URL}/chatHistoryTempares/${userInfluencerId}?${params.toString()}`,
      );
      throwErrorIfWrongStatus(response, 200);
      const { userInfluencerData, data, hasMore, nextCursor } = response.data.data;
      return { userInfluencerData, data, hasMore, nextCursor };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching influencer chat.');
    }
  };

  sendMessage = async (userInfluencerId, body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(
        `${BASE_URL}/send-message/${userInfluencerId}?${params.toString()}`,
        body,
      );
      throwErrorIfWrongStatus(response, 200);
      const { correctedText } = response.data.data;
      return { correctedText };
    } catch (err) {
      throw handleError(err, 'Error occurred while sending text message.');
    }
  };

  sendMassDMMessage = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/send-mass-dm-message?${params.toString()}`, body);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while sending mass DM message.');
    }
  };

  pinUser = async (userInfluencerId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/pin/${userInfluencerId}?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while pin user.');
    }
  };

  editDisplayName = async (userInfluencerId, name, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.put(`${BASE_URL}/display-name/${userInfluencerId}?${params.toString()}`, {
        name,
      });
      throwErrorIfWrongStatus(response, 200);
      return { name };
    } catch (err) {
      throw handleError(err, 'Error occurred while editing display name.');
    }
  };

  markChatAsRead = async (userInfluencerId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(
        `${BASE_URL}/chatHistoryReadares/${userInfluencerId}?${params.toString()}`,
      );
      throwErrorIfWrongStatus(response, 200);
      return { userInfluencerId };
    } catch (err) {
      throw handleError(err, 'Error occurred while mark chat as read.');
    }
  };

  addUserToCollection = async (userInfluencerId, collectionId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(
        `${BASE_URL}/add-to-collection/${userInfluencerId}?${params.toString()}`,
        { collectionId },
      );
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while adding user to collection.');
    }
  };

  fetchNotifications = async (page, perPage, influencerId) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('perPage', perPage);
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.get(`${BASE_URL}/notifications?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data, total } = response.data.data;
      return { data, total };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching notifications.');
    }
  };

  fetchInfluencerNotificationStatus = async influencerId => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.get(`${BASE_URL}/notifications-status?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { newNotifications } = response.data.data;
      return { newNotifications };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching influencer notification status.');
    }
  };

  toggleUserInfluencerManualChat = async (userInfluencerId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(
        `${BASE_URL}/toggle-manual-chat/${userInfluencerId}?${params.toString()}`,
      );
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while toggling influencer manual chat.');
    }
  };

  getAiChatSuggestions = async (userInfluencerId, influencerId, manualFlowInstructionType) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(
        `${BASE_URL}/aiChatSuggestions/${userInfluencerId}?${params.toString()}`,
        { manualFlowInstructionType },
      );
      throwErrorIfWrongStatus(response, 200);
      const { suggestions } = response.data.data;
      return { suggestions };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching ai suggestions.');
    }
  };

  unblockUserInfluencerChat = async (userInfluencerId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/unblock/${userInfluencerId}?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while unblocking influencer chat.');
    }
  };

  fetchInfluencerMassDmHistory = async (page, influencerId) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/mass-dm-history?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data, total, perPage: perPageResponse } = response.data.data;
      return { data, maxReached: total <= +page * +perPageResponse, page };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching mass dm history.');
    }
  };

  cancelInfluencerManualMassDm = async (massDmId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/cancel-mass-dm/${massDmId}?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      return { massDmId };
    } catch (err) {
      throw handleError(err, 'Error occurred while canceling mass dm.');
    }
  };
}

export default new CollectionService();
